import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'
import { useFlash } from '../motion'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  elevation?: 'flat' | 'raised' | 'float'
  /** A quiet sunken panel: paper fill, no hairline, no shadow. */
  tint?: boolean
  children?: ReactNode
}

export function Card({ elevation = 'flat', tint, className, ...rest }: CardProps) {
  return (
    <div
      className={cx(
        'card',
        elevation === 'raised' && 'card-raised',
        elevation === 'float' && 'card-float',
        tint && 'card-tint',
        className,
      )}
      {...rest}
    />
  )
}

Card.Header = function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('card-h', className)} {...rest} />
}

Card.Body = function CardBody({
  tight,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { tight?: boolean }) {
  return <div className={cx(tight ? 'card-b-tight' : 'card-b', className)} {...rest} />
}

Card.Footer = function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('card-f', className)} {...rest} />
}

export function Alert({
  tone,
  icon,
  className,
  children,
}: {
  tone: 'info' | 'success' | 'warning' | 'danger'
  icon?: IconName
  className?: string
  children: ReactNode
}) {
  const fallback: Record<string, IconName> = {
    info: 'info',
    success: 'check',
    warning: 'alert',
    danger: 'alert',
  }
  return (
    <div className={cx('alert', `alert-${tone}`, className)} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon name={icon ?? fallback[tone]} />
      <div>{children}</div>
    </div>
  )
}

export function Empty({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon: IconName
  title: string
  body?: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('empty', className)}>
      <div className="glyph">
        <Icon name={icon} />
      </div>
      <div className="t-h4">{title}</div>
      {body && <div className="t-sm t-muted">{body}</div>}
      {action}
    </div>
  )
}

export function Skeleton({
  w = '100%',
  h = 14,
  radius,
  className,
}: {
  w?: string | number
  h?: number
  radius?: string
  className?: string
}) {
  return (
    <div
      className={cx('skel', className)}
      style={{ inlineSize: w, blockSize: h, borderRadius: radius }}
      aria-hidden="true"
    />
  )
}

export function Kpi({
  label,
  value,
  delta,
  className,
}: {
  label: string
  value: ReactNode
  delta?: { text: ReactNode; direction: 'up' | 'down' | 'flat' }
  className?: string
}) {
  // A figure given as text or a number flashes when a refetch moves it.
  const moved = useFlash(typeof value === 'string' || typeof value === 'number' ? value : undefined)
  return (
    <div className={cx('kpi', className)}>
      <div className="kpi-l">{label}</div>
      <div key={moved.key} className={cx('kpi-v', moved.className)}>
        {value}
      </div>
      {delta && (
        <div className={cx('kpi-d', delta.direction === 'up' && 't-buy', delta.direction === 'down' && 't-sell')}>
          {delta.direction !== 'flat' && (
            <Icon name={delta.direction === 'up' ? 'arrow-up' : 'arrow-down'} size="sm" />
          )}
          {delta.text}
        </div>
      )}
    </div>
  )
}
